import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tab,
  Tabs,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Chip,
  IconButton,
  Paper,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import { Attachment } from '../types/api';
import type { DocumentSummary, Document, DocumentVersionInfo } from '../types/documents';
import { DocumentsList, DocumentViewer, DocumentVersionHistory } from './documents';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`conversation-tabpanel-${index}`}
      aria-labelledby={`conversation-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `conversation-tab-${index}`,
    'aria-controls': `conversation-tabpanel-${index}`,
  };
}

interface ConversationDashboardModalProps {
  open: boolean;
  onClose: () => void;
  attachments: Attachment[];
  documents?: DocumentSummary[];
  initialTab?: number;
  selectedAttachmentId?: string;
  selectedDocumentId?: string;
  onAttachmentSelect?: (attachment: Attachment) => void;
  onDocumentSelect?: (document: DocumentSummary) => void;
  onLoadDocument?: (documentId: string, version?: number) => Promise<Document | null>;
  onLoadDocumentVersions?: (documentId: string) => Promise<DocumentVersionInfo[]>;
}

const ConversationDashboardModal: React.FC<ConversationDashboardModalProps> = ({
  open,
  onClose,
  attachments,
  documents = [],
  initialTab = 0, // Default to Attachments tab (now index 0)
  selectedAttachmentId,
  selectedDocumentId,
  onAttachmentSelect,
  onDocumentSelect,
  onLoadDocument,
  onLoadDocumentVersions,
}) => {
  const [tabValue, setTabValue] = useState(initialTab);
  const [selectedAttachment, setSelectedAttachment] = useState<Attachment | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Document state
  const [selectedDocSummary, setSelectedDocSummary] = useState<DocumentSummary | null>(null);
  const [currentDocument, setCurrentDocument] = useState<Document | null>(null);
  const [documentVersions, setDocumentVersions] = useState<DocumentVersionInfo[]>([]);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Update tab when initialTab prop changes
  useEffect(() => {
    setTabValue(initialTab);
  }, [initialTab]);

  // Set initial selected attachment
  useEffect(() => {
    if (selectedAttachmentId && attachments.length > 0) {
      const attachment = attachments.find(att => att.id === selectedAttachmentId);
      if (attachment) {
        setSelectedAttachment(attachment);
        setTabValue(1); // Switch to attachments tab
      }
    }
  }, [selectedAttachmentId, attachments]);

  // If no attachment is selected, select the first one when attachments are loaded
  useEffect(() => {
    if (!selectedAttachment && attachments.length > 0) {
      setSelectedAttachment(attachments[0]);
    }
  }, [attachments, selectedAttachment]);

  // Load document content when selected - defined before useEffect that uses it
  const loadDocumentContent = useCallback(async (docSummary: DocumentSummary, version?: number) => {
    if (!onLoadDocument) return;

    setLoadingDocument(true);
    try {
      const doc = await onLoadDocument(docSummary.documentId, version);
      setCurrentDocument(doc);

      // Also load version history if available
      if (onLoadDocumentVersions) {
        const versions = await onLoadDocumentVersions(docSummary.documentId);
        setDocumentVersions(versions);
      }
    } catch (error) {
      console.error('Failed to load document:', error);
    } finally {
      setLoadingDocument(false);
    }
  }, [onLoadDocument, onLoadDocumentVersions]);

  // Set initial selected document and load its content
  useEffect(() => {
    if (selectedDocumentId && onLoadDocument) {
      // Try to find in the existing documents array first
      const doc = documents.find(d => d.documentId === selectedDocumentId);

      if (doc) {
        // Document found in array - select it and load content
        setSelectedDocSummary(doc);
        setTabValue(1); // Switch to documents tab
        void loadDocumentContent(doc);
      } else {
        // Document not in array (just created) - load directly by ID
        setTabValue(1); // Switch to documents tab
        setLoadingDocument(true);

        onLoadDocument(selectedDocumentId)
          .then((loadedDoc) => {
            if (loadedDoc) {
              setCurrentDocument(loadedDoc);
              // Create a summary from the loaded document
              setSelectedDocSummary({
                documentId: loadedDoc.documentId,
                version: loadedDoc.version,
                title: loadedDoc.title,
                templateName: loadedDoc.templateName,
                status: loadedDoc.status,
                createdAt: loadedDoc.createdAt,
                updatedAt: loadedDoc.updatedAt,
                versionCount: loadedDoc.version, // Assume current version is latest
              });
              // Also load version history
              if (onLoadDocumentVersions) {
                void onLoadDocumentVersions(selectedDocumentId).then(setDocumentVersions);
              }
            }
          })
          .catch((error) => {
            console.error('Failed to load document:', error);
          })
          .finally(() => {
            setLoadingDocument(false);
          });
      }
    }
  }, [selectedDocumentId, documents, onLoadDocument, onLoadDocumentVersions, loadDocumentContent]);

  // Handle document selection
  const handleDocumentSelect = useCallback((docSummary: DocumentSummary) => {
    setSelectedDocSummary(docSummary);
    onDocumentSelect?.(docSummary);
    void loadDocumentContent(docSummary);
  }, [onDocumentSelect, loadDocumentContent]);

  // Handle version selection from history
  const handleVersionSelect = useCallback((version: number) => {
    if (selectedDocSummary) {
      void loadDocumentContent(selectedDocSummary, version);
      setShowVersionHistory(false);
    }
  }, [selectedDocSummary, loadDocumentContent]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAttachmentSelect = (attachment: Attachment) => {
    setSelectedAttachment(attachment);
    if (onAttachmentSelect) {
      onAttachmentSelect(attachment);
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    if (!attachment.download_url) {
      console.error('No download URL available for attachment:', attachment.id);
      return;
    }

    setDownloadingId(attachment.id);
    try {
      // Create a temporary link element and trigger download
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      const link = document.createElement('a') as HTMLAnchorElement;
      link.href = attachment.download_url;
      link.download = attachment.filename;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      document.body.appendChild(link);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      link.click();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      // TODO: Show error toast
    } finally {
      setDownloadingId(null);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <ImageIcon />;
    if (mimeType === 'application/pdf') return <PdfIcon />;
    if (mimeType.includes('document') || mimeType.includes('text')) return <DocIcon />;
    return <FileIcon />;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'UPLOADED':
      case 'PROCESSED':
        return <CheckCircleIcon color="success" />;
      case 'PENDING_UPLOAD':
      case 'PROCESSING':
        return <ScheduleIcon color="warning" />;
      case 'ERROR':
        return <ErrorIcon color="error" />;
      default:
        return <ScheduleIcon />;
    }
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '80vh',
          minHeight: '600px',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Conversation Dashboard
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="conversation dashboard tabs">
          <Tab
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AttachFileIcon fontSize="small" />
                Attachments
                {attachments.length > 0 && (
                  <Chip size="small" label={attachments.length} />
                )}
              </span>
            }
            {...a11yProps(0)}
          />
          <Tab
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ArticleIcon fontSize="small" />
                Documents
                {documents.length > 0 && (
                  <Chip size="small" label={documents.length} />
                )}
              </span>
            }
            {...a11yProps(1)}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, height: '100%' }}>
        <TabPanel value={tabValue} index={0}>
          {attachments.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">
                <Typography variant="body1">
                  No attachments in this conversation yet.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Upload files by dragging and dropping them into the chat area or using the attachment button.
                </Typography>
              </Alert>
            </Box>
          ) : (
            <Box sx={{ height: '100%', display: 'flex' }}>
              {/* Attachments List */}
              <Box sx={{ 
                width: '320px', 
                borderRight: 1, 
                borderColor: 'divider',
                overflow: 'auto'
              }}>
                <List sx={{ p: 0 }}>
                  {attachments.map((attachment) => (
                    <ListItem key={attachment.id} sx={{ p: 0 }}>
                      <ListItemButton
                        selected={selectedAttachment?.id === attachment.id}
                        onClick={() => handleAttachmentSelect(attachment)}
                        sx={{ py: 2 }}
                      >
                        <ListItemIcon>
                          {getFileIcon(attachment.mime_type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={attachment.filename}
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                {formatFileSize(attachment.size)}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                {getStatusIcon(attachment.status)}
                                <Typography variant="caption" color="text.secondary">
                                  {attachment.status}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Attachment Details */}
              <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
                {selectedAttachment ? (
                  <Box>
                    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getFileIcon(selectedAttachment.mime_type)}
                          <Typography variant="h6">{selectedAttachment.filename}</Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          startIcon={downloadingId === selectedAttachment.id ? <CircularProgress size={16} /> : <DownloadIcon />}
                          onClick={() => handleDownload(selectedAttachment)}
                          disabled={downloadingId === selectedAttachment.id || !selectedAttachment.download_url}
                        >
                          Download
                        </Button>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <Chip label={`Type: ${selectedAttachment.mime_type}`} variant="outlined" size="small" />
                        <Chip label={`Size: ${formatFileSize(selectedAttachment.size)}`} variant="outlined" size="small" />
                        <Chip 
                          label={`Status: ${selectedAttachment.status}`} 
                          variant="outlined" 
                          size="small"
                          color={selectedAttachment.status === 'PROCESSED' ? 'success' : 'default'}
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary">
                        Uploaded: {new Date(selectedAttachment.created_at).toLocaleString()}
                      </Typography>
                    </Paper>

                    {selectedAttachment.extracted_content && (
                      <Paper elevation={1} sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Extracted Content
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box 
                          sx={{ 
                            maxHeight: '400px',
                            overflow: 'auto',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                            backgroundColor: 'grey.50',
                            p: 2,
                            borderRadius: 1,
                          }}
                        >
                          {selectedAttachment.extracted_content}
                        </Box>
                      </Paper>
                    )}

                    {!selectedAttachment.extracted_content && selectedAttachment.status === 'UPLOADED' && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Content extraction is in progress. Please check back in a moment.
                      </Alert>
                    )}

                    {selectedAttachment.status === 'ERROR' && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        There was an error processing this file. Please try uploading it again.
                      </Alert>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <AttachFileIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
                      Select an attachment to view details
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </TabPanel>

        {/* Documents Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ height: '100%', display: 'flex' }}>
            {/* Documents List */}
            <Box
              sx={{
                width: '320px',
                borderRight: 1,
                borderColor: 'divider',
                overflow: 'auto',
              }}
            >
              <DocumentsList
                documents={documents}
                selectedDocumentId={selectedDocSummary?.documentId}
                onDocumentSelect={handleDocumentSelect}
              />
            </Box>

            {/* Document Viewer */}
            <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
              {loadingDocument ? (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <DocumentViewer
                  document={currentDocument}
                  onShowVersionHistory={() => setShowVersionHistory(true)}
                />
              )}
            </Box>
          </Box>
        </TabPanel>
      </DialogContent>

      {/* Document Version History Dialog */}
      <DocumentVersionHistory
        open={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        versions={documentVersions}
        currentVersion={currentDocument?.version ?? 1}
        documentTitle={currentDocument?.title ?? ''}
        onVersionSelect={handleVersionSelect}
      />

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConversationDashboardModal;
